import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import type { paths } from '../schema';
import type {
  CustomObjectField,
  CustomObjectFieldRequest,
  Field,
} from './custom-object';
import type { RequestOptions, RequestOptionsWithParams } from './types';
import { curry1 } from './utils';

export type SearchFieldsOptions = RequestOptionsWithParams<
  paths['/api/pipelines/{object_identifier}/fields/search']['post']['parameters']['query']
> & {
  body?: paths['/api/pipelines/{object_identifier}/fields/search']['post']['requestBody']['content']['application/json'];
};

export type GetAllPipelineFieldsParams =
  paths['/api/pipelines/{object_identifier}/fields/settings-search']['get']['parameters']['query'];

export type FieldCategory =
  paths['/api/pipelines/{object_pk}/categories']['get']['responses']['200']['content']['application/json'][number];

export const createPipelineObjectField = async (
  instance: AxiosInstance,
  customObjectId: string,
  data: CustomObjectFieldRequest,
  options: RequestOptions = {}
): Promise<Field> => {
  const res = await instance.post(
    `/pipelines/${customObjectId}/fields`,
    data,
    options
  );

  return res.data;
};

/**
 * @remarks this bypasses permissions to return all custom object fields
 */
export const getAllPipelineObjectFields = async (
  instance: AxiosInstance,
  objectId: string,
  options?: RequestOptionsWithParams<GetAllPipelineFieldsParams>
): Promise<CustomObjectField[]> => {
  const res = await instance.get<CustomObjectField[]>(
    `/pipelines/${objectId}/fields/settings-search`,
    options
  );

  return res.data;
};

export const getPipelineObjectCategories = async (
  instance: AxiosInstance,
  objectId: string,
  options?: RequestOptions
): Promise<FieldCategory[]> => {
  const res = await instance.get<FieldCategory[]>(
    `/pipelines/${objectId}/categories`,
    options
  );

  return res.data;
};

export const getPipelineObjectFields = async (
  instance: AxiosInstance,
  objectId: string,
  options: SearchFieldsOptions = {}
): Promise<CustomObjectField[]> => {
  const { body, params, ...opts } = options;

  const config: AxiosRequestConfig = { ...opts, params };

  const res = await instance.post<CustomObjectField[]>(
    `/pipelines/${objectId}/fields/search`,
    body,
    config
  );

  return res.data;
};

export class PipelineObjectService {
  public createPipelineObjectField;
  public getAllPipelineObjectFields;
  public getPipelineObjectCategories;
  public getPipelineObjectFields;

  constructor(private instance: AxiosInstance) {
    this.createPipelineObjectField = curry1(createPipelineObjectField)(
      this.instance
    );
    this.getAllPipelineObjectFields = curry1(getAllPipelineObjectFields)(
      this.instance
    );
    this.getPipelineObjectCategories = curry1(getPipelineObjectCategories)(
      this.instance
    );
    this.getPipelineObjectFields = curry1(getPipelineObjectFields)(
      this.instance
    );
  }
}
